import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FiAward, FiArrowDown } from "react-icons/fi";
import "./about.css";

const About = () => {
  // Intersection Observer scroll trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    const animElements = document.querySelectorAll(".animate-on-scroll");
    animElements.forEach((el) => observer.observe(el));

    return () => {
      animElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const team = [
    {
      name: "Craig Tashman",
      role: "CEO & Founder",
      desc: "iOS development and UX design. Craig holds a PhD from Georgia Tech and leads the visual design and product vision.",
      avatar: "👨‍💻",
      subrole: "UX Design Lead",
    },
    {
      name: "Cristiano Ghersi",
      role: "CTO",
      desc: "Backend development and system architecture. Cristiano is a PhD holder and a serial entrepreneur.",
      avatar: "⚙️",
      subrole: "Architecture Lead",
    },
    {
      name: "Stephen Dukker",
      role: "Co-Founder",
      desc: "Investor, co-founder, and strategic advisor with extensive experience guiding early-stage tech scaleups.",
      avatar: "💼",
      subrole: "Strategic Advisor",
    },
    {
      name: "Dalas Verdugo",
      role: "Customer Relations",
      desc: "Heads startup community development, user feedback loops, and customer success management.",
      avatar: "🤝",
      subrole: "Community Lead",
    },
    {
      name: "Warren Agin",
      role: "Strategic Advisor",
      desc: "Strategic advisor focused on legal community alignments and integration with corporate legal technology systems.",
      avatar: "⚖️",
      subrole: "Legal Tech Advisor",
    },
  ];

  const missionBullets = [
    { text: "Ask questions", icon: "❓" },
    { text: "Find evidence", icon: "🔍" },
    { text: "Compare information", icon: "📊" },
    { text: "Make observations", icon: "✍️" },
    { text: "Build connections", icon: "🔗" },
    { text: "Develop ideas", icon: "💡" },
    { text: "Form conclusions", icon: "🏁" },
  ];
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth"
    });
  }, [pathname]);

  return (
    <div className="about-page-wrapper">
      {/* ==================== 1. HERO SECTION ==================== */}
      <section className="about-hero">
        <div className="container hero-grid">
          <div className="hero-text animate-on-scroll fade-up">
            <span className="badge-pill">OUR VISION</span>
            <h1>
              Rethinking the Way <br />
              <span className="highlight-text">We Read</span>
            </h1>
            <p className="hero-desc">
              Paper gave us a simple way to read, highlight, annotate, and
              organize information. But today's documents are longer, more
              complex, and spread across multiple digital formats.
            </p>
            <p className="hero-highlight-sub">
              LiquidText was created to rethink that workflow from the ground
              up.
            </p>
            <div className="hero-btn-box">
              <a href="#story" className="btn-primary">
                Read Our Story <FiArrowDown className="btn-icon" />
              </a>
            </div>
          </div>

          <div className="hero-visual animate-on-scroll fade-left">
            <div className="about-visual-card shadow-lg">
              <img
                src="/images/why_read_faster.png"
                alt="Active Document Reading in LiquidText"
                className="about-visual-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 2. OUR STORY SECTION ==================== */}
      <section id="story" className="story-section bg-light">
        <div className="container story-grid">
          <div className="story-visual animate-on-scroll fade-right">
            <div className="about-visual-card shadow-lg">
              <img
                src="/images/why_connect_ideas.png"
                alt="Active document connection and ideation research"
                className="about-visual-img"
              />
            </div>
          </div>

          <div className="story-text animate-on-scroll fade-left">
            <span className="section-tag-light">THE FOUNDATION</span>
            <h2>From Research to LiquidText</h2>
            <p>
              LiquidText grew out of research into{" "}
              <strong>active reading</strong>—the process of actively
              highlighting, annotating, searching, connecting, and taking notes
              while working with complex information.
            </p>
            <p>
              Founder Craig Tashman, who earned his PhD from Georgia Tech,
              conducted research into interfaces that could overcome the
              limitations of traditional paper-based reading and document
              analysis. That research eventually led to the creation of
              LiquidText.
            </p>

            <div className="story-quote-card">
              <div className="quote-mark">“</div>
              <p className="quote-text">
                The goal was simple: Make digital documents work more like the
                way people actually think.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 3. MISSION SECTION ==================== */}
      <section className="mission-section">
        <div className="container grid-two-col">
          <div className="mission-text animate-on-scroll fade-right">
            <span className="section-tag">OUR MISSION</span>
            <h2>Make Complex Information Easier to Understand</h2>
            <p className="mission-desc">
              We believe reading shouldn't be a passive experience. When you
              read an important document, you don't simply consume information.
            </p>
            <p className="mission-lead">
              LiquidText is designed to support the entire lifecycle of document
              analysis, helping you to actively:
            </p>

            <div className="mission-bullets-grid">
              {missionBullets.map((bullet, idx) => (
                <div key={idx} className="mission-bullet-card">
                  <span className="bullet-card-icon">{bullet.icon}</span>
                  <span className="bullet-card-text">{bullet.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mission-visual animate-on-scroll fade-left">
            <div className="about-visual-card shadow-lg">
              <img
                src="/images/liquidtext_active.png"
                alt="LiquidText Interactive Active Reading Workspace"
                className="about-visual-img"
              />
            </div>
            <div className="ws-caption text-center">
              Active workspace supporting deep analytical synthesis.
            </div>
          </div>
        </div>
      </section>

      {/* ==================== 4. TEAM SECTION ==================== */}
      <section className="team-section bg-light">
        <div className="container">
          <div className="section-header text-center animate-on-scroll fade-up">
            <span className="section-tag-light">OUR PEOPLE</span>
            <h2>The LiquidText Team</h2>
            <p className="section-desc max-600">
              Meet the researchers, designers, developers, and advisors building
              the next generation of workspace productivity.
            </p>
          </div>

          <div className="team-cards-grid">
            {team.map((member, idx) => (
              <div
                key={idx}
                className="team-member-card animate-on-scroll fade-up"
              >
                <div className="team-avatar-box">
                  <span className="team-avatar">{member.avatar}</span>
                </div>
                <h3>{member.name}</h3>
                <span className="team-member-role">{member.role}</span>
                <span className="team-member-subrole">{member.subrole}</span>
                <p className="team-member-desc">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== 5. RECOGNITION SECTION ==================== */}
      <section className="recognition-section">
        <div className="container recognition-layout">
          <div className="recognition-text animate-on-scroll fade-right">
            <span className="section-tag">
              <FiAward /> GLOBAL RECOGNITION
            </span>
            <h2>Industry Praise & Platform Awards</h2>
            <p className="lead-recognition">
              LiquidText has received recognition from major technology
              publications and app platforms, including Apple's App Store
              Editors' Choice and recognition from Microsoft.
            </p>
            <div className="platform-badges">
              <div className="platform-badge-card">
                <strong> App Store</strong>
                <span>Editors' Choice Award</span>
              </div>
              <div className="platform-badge-card">
                <strong>❖ Microsoft</strong>
                <span>Featured Windows App</span>
              </div>
            </div>
          </div>

          <div className="recognition-visual animate-on-scroll fade-left">
            <div className="recognition-mockup-wrap shadow-lg">
              <img
                src="/images/work_across_devices.png"
                alt="LiquidText running on major tablet and desktop platforms"
                className="about-visual-img"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
